export type Status = 'LIVE' | 'COOLING' | 'FADING FAST' | 'LOST'

// Piecewise decay anchored to published research.
// Between anchors we interpolate EXPONENTIALLY so the cliff at 5:00 stays kinesthetic.
const anchors: Array<[seconds: number, probability: number]> = [
  [0, 100],
  [60, 95], // Velocify 391% lift window
  [300, 80], // 5-min "peak" cliff
  [600, 35], // InsideSales 8× drop after 5 min
  [1800, 5], // MIT 100× / 21× decay
  [3600, 2], // HBR 7× drop 1hr → 2hr
  [86400, 0.01], // HBR 60× drop 1hr → 24hr
]

export function getProbability(elapsedSeconds: number): number {
  const t = Math.max(0, elapsedSeconds)
  if (t <= anchors[0][0]) return anchors[0][1]
  if (t >= anchors[anchors.length - 1][0]) return 0

  for (let i = 0; i < anchors.length - 1; i++) {
    const [x0, y0] = anchors[i]
    const [x1, y1] = anchors[i + 1]
    if (t >= x0 && t <= x1) {
      const span = x1 - x0
      const u = (t - x0) / span
      if (y0 > 0 && y1 > 0) {
        return y0 * Math.pow(y1 / y0, u)
      }
      return y0 + (y1 - y0) * u
    }
  }
  return 0
}

export function getStatus(elapsedSeconds: number): Status {
  if (elapsedSeconds < 60) return 'LIVE'
  if (elapsedSeconds < 300) return 'COOLING'
  if (elapsedSeconds < 1800) return 'FADING FAST'
  return 'LOST'
}

export type StatusTheme = {
  text: string
  textHex: string
  bg: string
  border: string
  dot: string
}

export function getStatusTheme(status: Status): StatusTheme {
  switch (status) {
    case 'LIVE':
      return {
        text: 'text-emerald-600',
        textHex: '#059669',
        bg: 'bg-emerald-600',
        border: 'border-emerald-600',
        dot: 'bg-emerald-500',
      }
    case 'COOLING':
      return {
        text: 'text-amber-600',
        textHex: '#D97706',
        bg: 'bg-amber-500',
        border: 'border-amber-500',
        dot: 'bg-amber-500',
      }
    case 'FADING FAST':
      return {
        text: 'text-orange-600',
        textHex: '#EA580C',
        bg: 'bg-orange-600',
        border: 'border-orange-600',
        dot: 'bg-orange-500',
      }
    case 'LOST':
      return {
        text: 'text-rose-600',
        textHex: '#E11D48',
        bg: 'bg-rose-600',
        border: 'border-rose-700',
        dot: 'bg-rose-500',
      }
  }
}

export function getCitation(elapsedSeconds: number): string {
  if (elapsedSeconds < 300) {
    return 'Velocify LRM · n=3.5M leads · 391% lift window'
  }
  if (elapsedSeconds < 1800) {
    return 'InsideSales LRM · n=55M activities · 2021'
  }
  if (elapsedSeconds < 3600) {
    return 'MIT / InsideSales · n=15K leads · HBR 2011'
  }
  return 'HBR 2011 · n=2,241 firms · 60× drop 1hr → 24hr'
}

export function getInterpretation(status: Status): string {
  switch (status) {
    case 'LIVE':
      return 'Respond inside 60 seconds and you close 95 out of 100.'
    case 'COOLING':
      return "By minute 5, you've already lost 1 in 5 leads."
    case 'FADING FAST':
      return 'By minute 10, two-thirds are already gone to a competitor.'
    case 'LOST':
      return 'After 30 minutes, fewer than 1 in 20 still close.'
  }
}

export function formatTimer(seconds: number): string {
  const s = Math.max(0, Math.floor(seconds))
  if (s >= 3600) {
    const h = Math.floor(s / 3600)
    const m = Math.floor((s % 3600) / 60)
    const r = s % 60
    return `${pad(h)}:${pad(m)}:${pad(r)}`
  }
  const m = Math.floor(s / 60)
  const r = s % 60
  return `${pad(m)}:${pad(r)}`
}

function pad(n: number): string {
  return n.toString().padStart(2, '0')
}
